import { getContrastColor } from '@gettactic/helpers/src/colors/getContrastColor';
import React from 'react';
import { MapInteractiveZone, SpotShape } from '../../../../types';
import { useMapCanvas } from '../../contexts/MapCanvasContext';
import {
  DEFAULT_NODE_CIRCLE_RADIUS,
  DEFAULT_NODE_RECT_COLOR
} from '../../defines';
import { SpotLayout } from '../../types';
import { useShape } from '../ShapeContext/useShape';
import { InteractiveZoneSVG } from './InteractiveZoneSVG';

export interface SpotItemSVGProps {
  item: SpotShape;
  layout: SpotLayout;
  isHighlighted?: boolean;
  interactiveZone?: MapInteractiveZone;
}

export const SpotItemSVG: React.FC<SpotItemSVGProps> = ({
  item,
  layout,
  interactiveZone,
  isHighlighted
}) => {
  const { isViewMode, highlightResource, setHoveringNode } = useMapCanvas();
  const { isHovered, isSelected, shape: _shape, selectIndex } = useShape();
  const shape = _shape as SpotShape;
  const { locked: isLocked } = shape;
  const rectColor = shape.color || DEFAULT_NODE_RECT_COLOR;

  const labelColor = React.useMemo(() => {
    return getContrastColor(rectColor);
  }, [rectColor]);

  const shapeStroke = isHighlighted
    ? 'red'
    : !isLocked && (isHovered || isSelected)
      ? '#000000'
      : 'transparent';

  const strokeWidth = isHighlighted ? 2 : 0.5;
  const spotType =
    item.spotAppearance ||
    shape.spotAppearance ||
    item.spotType ||
    shape.spotType;
  const labelPlacement = item.labelPlacement || shape.labelPlacement;

  const hasRect = spotType === 'desk' || spotType === 'meeting_room';

  let cornerRadius = item.rectProps?.cornerRadius || 0;
  if (spotType === 'meeting_room') {
    cornerRadius = 15;
  }

  const circleRadius = item.circleProps?.radius || DEFAULT_NODE_CIRCLE_RADIUS;

  const textFontSize = shape.fontSize || 16;
  const textFontFamily = item.textProps?.fontFamily || 'Arial';
  const textFontStyle = item.textProps?.fontStyle || 'bold';
  const textRotation = item.textProps?.rotation ?? layout.text.rotation;
  const textColor =
    item.textProps?.color || (labelColor === 'light' ? 'white' : 'black');

  const shapeWidth = (item.textProps?.width ?? layout.text.width) || 0;

  // Calculate the transform for the entire spot group
  const transform = `translate(${shape.x}, ${shape.y})${shape.rotation ? ` rotate(${shape.rotation})` : ''}`;

  // Calculate proper text position based on alignment
  const getTextPosition = () => {
    let textX = layout.text.x + (item.textProps?.marginLeft || 0);
    
    // For center alignment, position text at the center of the rectangle
    if (shape.textAlign === 'center' && hasRect) {
      textX = layout.rect.x + layout.rect.width / 2;
    }
    // For right alignment, position text at the right edge of the rectangle
    else if (shape.textAlign === 'right' && hasRect) {
      textX = layout.rect.x + layout.rect.width;
    }
    
    return textX;
  };

  const textX = getTextPosition();

  return (
    <g id={item.id} className={'spot-shape-container'} transform={transform}>
      <g>
        {hasRect && (
          <rect
            x={layout.rect.x}
            y={layout.rect.y}
            width={layout.rect.width}
            height={layout.rect.height}
            transform={`rotate(${layout.rect.rotation} ${layout.rect.x + layout.rect.width / 2} ${layout.rect.y + layout.rect.height / 2})`}
            rx={cornerRadius}
            fill={rectColor}
            opacity={item.rectProps?.opacity || 1}
            stroke={shapeStroke}
            strokeWidth={strokeWidth}
          />
        )}
        {labelPlacement !== 'hidden' && (
          <text
            x={textX}
            y={
              layout.text.y +
              (item.textProps?.marginTop || 0) -
              (spotType === 'meeting_room'
                ? textFontSize > 26
                  ? textFontSize / 2
                  : 0
                : 0)
            }
            transform={`rotate(${textRotation} ${textX} ${layout.text.y})`}
            fontSize={textFontSize}
            fontFamily={textFontFamily}
            fontStyle={textFontStyle}
            fill={textColor}
            textAnchor={
              shape.textAlign === 'center'
                ? 'middle'
                : shape.textAlign === 'right'
                  ? 'end'
                  : 'start'
            }
            dominantBaseline="middle"
          >
            {item.label}
          </text>
        )}
      </g>
      <InteractiveZoneSVG
        interactiveZone={interactiveZone}
        shape={shape}
        layout={layout}
        selectIndex={selectIndex}
        isActive={!shape.isInactive}
      />
      {selectIndex === undefined && !isViewMode && (
        <circle
          cx={layout.circle.x}
          cy={layout.circle.y}
          r={5}
          fill={item.resource_id ? '#60bb10' : 'orange'}
          opacity={0.5}
        />
      )}
      {selectIndex !== undefined && (
        <g>
          <circle
            cx={layout.circle.x}
            cy={layout.circle.y}
            r={circleRadius}
            fill="#2563eb"
            stroke="#ffffff"
            strokeWidth={2}
          />
          <text
            x={layout.circle.x}
            y={layout.circle.y}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize={12}
            fontFamily="Arial"
            fontWeight="bold"
            fill="#ffffff"
          >
            {String(selectIndex + 1)}
          </text>
        </g>
      )}
    </g>
  );
};
